import { t } from 'i18next';
import { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { Form, FormField } from 'core/components';
import { InputType } from 'core/enums';
import { FormProvider } from 'core/states';
import { getEmptyErrorMessage } from 'core/yup';
import { useProjectPricingProfileActions } from 'projects/hooks';
import { ICreateProjectPricingProfileForm } from 'projects/types';

interface IProps {
  projectId: number;
  isOpen: boolean;
  onClose: () => void;
  defaultName?: string;
}

const schema = () =>
  yup.object({
    agreementId: yup.string().trim().nullable(),
    name: yup
      .string()
      .nullable()
      .required(getEmptyErrorMessage(t('labelName'))),
    shareAccess: yup.boolean().nullable(),
  });

export const CreateProjectPricingProfile = ({
  projectId,
  isOpen,
  onClose,
  defaultName,
}: IProps) => {
  const { t } = useTranslation();

  const { createAndLinkPricingProfile } = useProjectPricingProfileActions({ projectId });

  const defaultValues = {
    agreementId: null,
    name: defaultName || null,
    shareAccess: false,
  };

  const onSubmit =
    (reset: UseFormReset<ICreateProjectPricingProfileForm>) =>
    async (data: ICreateProjectPricingProfileForm) => {
      const { success } = await createAndLinkPricingProfile(data);

      if (!success) {
        return;
      }

      setTimeout(() => {
        handleOnClose(reset);
      }, 1000);
    };

  const handleOnClose = (reset?: UseFormReset<ICreateProjectPricingProfileForm>) => {
    reset?.(defaultValues);
    onClose();
  };

  return (
    <FormProvider schema={schema()} defaultValues={defaultValues} resetDefaultValues>
      <Form
        isOpen={isOpen}
        onClose={handleOnClose}
        title={t('titleCreateProjectPricingProfile')}
        onSubmit={onSubmit}
        submitBtnLabel={t('buttonLabelCreate')}
      >
        <FormField
          name="name"
          type={InputType.TEXT_INPUT}
          inputProps={{
            label: t('labelName'),
            required: true,
          }}
        />

        <FormField
          name="agreementId"
          type={InputType.TEXT_INPUT}
          inputProps={{
            label: t('labelAgreementId'),
          }}
        />

        <FormField
          name="shareAccess"
          type={InputType.CHECKBOX}
          inputProps={{ label: t('messagePricingProfileShareAccess') }}
        />
      </Form>
    </FormProvider>
  );
};
